import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import {
  ALL_TASKS_BRAND_PREFIX,
  ALL_TASKS_PREFIX,
  getTaskIdFromWindow,
  TASKS_PREFIX,
  URL_PARAM_OPEN_TASKS,
  URL_PARAM_VIEW_ALL_BRAND_TASKS,
  URL_PARAM_VIEW_ALL_TASKS,
} from "~/pages/dashboard/task/tab-windows/utils/window-helpers";
import { tabWindowStorage } from "~/utils/tab-window-storage";
import {
  Direction,
  type DirectionType,
  type TabWindow,
  type TabWindowState,
} from "../../task.types";
import type { AddWindowOptions } from "../context/tab-windows-context";

type Props<T> = {
  setWindows: React.Dispatch<React.SetStateAction<TabWindow<T>[]>>;
};

export const useWindowActions = <T>({ setWindows }: Props<T>) => {
  const [, setSearchParams] = useSearchParams();

  const addWindow = useCallback(
    (items: T[], id?: string, title?: string, options?: AddWindowOptions) => {
      const nowTime = Date.now();
      const newId = id ?? `window-${nowTime}`;
      const newStateId = id ?? `window-${nowTime}`;

      const { insertAfterWindowId: insertAfterId, ...restOptions } = options || {};

      setWindows((prev) => {
        const existing = prev.find((w) => w.id === newId);
        if (existing) {
          return prev.map((w) =>
            w.id === newId
              ? {
                  ...w,
                  items,
                  state: {
                    ...w.state,
                    title: title ?? w.state.title,
                    ...restOptions,
                  },
                }
              : w
          );
        }

        const newWindow: TabWindow<T> = {
          id: newId,
          items: items,
          originalItems: items,
          state: {
            id: newStateId,
            title: title ?? `Task Explorer ${prev.length + 1}`,
            ...restOptions,
            isOpen: true,
            isMinimized: false,
            isPinned: false,
            order: 0,
          },
        };

        const newWindows = [...prev];
        const insertIndex = newWindows.findIndex((w) => w.id === insertAfterId);

        if (insertIndex !== -1) {
          newWindows.splice(insertIndex + 1, 0, newWindow);
        } else {
          newWindows.push(newWindow);
        }

        const updatedWindows: TabWindow<T>[] = newWindows.map((window, index) => ({
          ...window,
          state: {
            ...window.state,
            order: index,
          },
        }));

        if (insertIndex !== -1) {
          updatedWindows.forEach((window) => {
            if (window?.state?.isPinned) {
              tabWindowStorage.updateWindow(window.id, { order: window.state.order });
            }
          });
        }

        return updatedWindows;
      });
    },
    [setWindows]
  );

  const setNewSearchParamsForWindow = useCallback(
    (windowIdValue: string[], prefix: string) => {
      setSearchParams((prev) => {
        const currentSearchParam = prev.get(prefix);
        if (!currentSearchParam) return prev;

        const urlIds = currentSearchParam.split(",").filter(Boolean);
        const newUrlIds = urlIds.filter((id) => !windowIdValue.includes(id));

        const newParams = new URLSearchParams(prev);
        if (newUrlIds.length > 0) {
          newParams.set(prefix, newUrlIds.join(","));
        } else {
          newParams.delete(prefix);
        }
        return newParams;
      });
    },
    [setSearchParams]
  );

  const closeWindow = useCallback(
    (windowId: string) => {
      setWindows((prev) => {
        const windowToClose = prev.find((w) => w.id === windowId);
        if (windowToClose?.state?.isPinned) {
          tabWindowStorage.removePinnedWindow(windowId);
        }
        return prev.filter((window) => window.id !== windowId);
      });

      if (windowId.startsWith(ALL_TASKS_BRAND_PREFIX)) {
        const id = getTaskIdFromWindow(windowId, ALL_TASKS_BRAND_PREFIX);
        setNewSearchParamsForWindow([String(id)], URL_PARAM_VIEW_ALL_BRAND_TASKS);
      } else if (windowId.startsWith(ALL_TASKS_PREFIX)) {
        const id = getTaskIdFromWindow(windowId, ALL_TASKS_PREFIX);
        setNewSearchParamsForWindow([String(id)], URL_PARAM_VIEW_ALL_TASKS);
      } else if (windowId.startsWith(TASKS_PREFIX)) {
        const id = getTaskIdFromWindow(windowId);
        setNewSearchParamsForWindow([String(id)], URL_PARAM_OPEN_TASKS);
      }
    },
    [setWindows, setNewSearchParamsForWindow]
  );

  const updateWindowState = useCallback(
    (windowId: string, newState: TabWindowState) => {
      setWindows((prev) => {
        const oldWindow = prev.find((w) => w.id === windowId);
        const wasPinned = oldWindow?.state?.isPinned ?? false;
        const isPinned = newState.isPinned;

        if (!wasPinned && isPinned) {
          tabWindowStorage.addPinnedWindow(newState);
        } else if (wasPinned && !isPinned) {
          tabWindowStorage.removePinnedWindow(windowId);
        } else if (isPinned) {
          tabWindowStorage.updateWindow(windowId, {
            isMinimized: newState.isMinimized,
            isOpen: newState.isOpen,
            order: newState.order,
            sortType: newState.sortType,
            title: newState.title,
            taskIds: newState.taskIds,
            appliedListIds: newState.appliedListIds,
            subtitle: newState.subtitle,
          });
        }

        return prev.map((window) =>
          window.id === windowId ? { ...window, state: newState } : window
        );
      });
    },
    [setWindows]
  );

  const changeWindowOrder = useCallback(
    (windowId: string, direction: DirectionType) => {
      setWindows((prev) => {
        const windowIndex = prev.findIndex((w) => w.id === windowId);
        if (windowIndex === -1) return prev;

        const newIndex =
          direction === Direction.Left
            ? Math.max(0, windowIndex - 1)
            : Math.min(prev.length - 1, windowIndex + 1);

        if (newIndex === windowIndex) return prev;

        const newWindows = [...prev];
        const [movedWindow] = newWindows.splice(windowIndex, 1);
        if (!movedWindow) return prev;

        newWindows.splice(newIndex, 0, movedWindow);

        const updatedWindows: TabWindow<T>[] = newWindows
          .filter((w): w is TabWindow<T> & { state: TabWindowState } => w.state !== undefined)
          .map((window, index) => ({
            ...window,
            state: {
              ...window.state,
              order: index,
            },
          }));

        updatedWindows.forEach((window) => {
          if (window?.state?.isPinned) {
            tabWindowStorage.updateWindow(window.id, { order: window.state.order });
          }
        });

        return updatedWindows;
      });
    },
    [setWindows]
  );

  const maximizeWindow = useCallback(
    (windowId: string) => {
      setWindows((prev) => {
        const updated = prev.map((window) => {
          if (window.id === windowId && window.state) {
            const newState = { ...window.state, isMinimized: false };
            if (newState.isPinned) {
              tabWindowStorage.updateWindow(windowId, { isMinimized: false });
            }
            return { ...window, state: newState };
          }
          return window;
        });
        return [...updated];
      });
    },
    [setWindows]
  );

  const togglePin = useCallback(
    (windowId: string, isPinned: boolean) => {
      setWindows((prev) =>
        prev.map((window) => {
          if (window.id === windowId && window.state) {
            const newState: TabWindowState = {
              ...window.state,
              isPinned,
            };

            if (isPinned) {
              tabWindowStorage.addPinnedWindow(newState);
            } else {
              tabWindowStorage.removePinnedWindow(windowId);
            }

            return { ...window, state: newState };
          }
          return window;
        })
      );
    },
    [setWindows]
  );

  const updateItems = useCallback(
    (items: T[], predicate?: (window: TabWindow<T>) => boolean) => {
      setWindows((prev) =>
        prev.map((window) => (!predicate || predicate(window) ? { ...window, items } : window))
      );
    },
    [setWindows]
  );

  return useMemo(
    () => ({
      addWindow,
      closeWindow,
      updateWindowState,
      changeWindowOrder,
      maximizeWindow,
      togglePin,
      updateItems,
    }),
    [
      addWindow,
      closeWindow,
      updateWindowState,
      changeWindowOrder,
      maximizeWindow,
      togglePin,
      updateItems,
    ]
  );
};
